import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Disclosure, Transition } from '@headlessui/react';
import { MenuIcon, XIcon } from '@heroicons/react/outline';
import Logo from '../common/Logo';
import { useModal } from '../../context/ModalContext';
import ThemeToggle from '../common/ThemeToggle';
import Button from '../common/Button';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Solutions', href: '/solutions' },
  { name: 'AI Basics', href: '/ai-basics' },
  { name: 'Team', href: '/team' },
  { name: 'Contact', href: '/contact' },
];

const Header: React.FC = () => {
  const { openGetStartedModal } = useModal();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  return (
    <Disclosure
      as="nav"
      className="floating-nav"
    >
      {({ open, close }) => (
        <>
          <div className="w-full">
            <div className="flex justify-between items-center h-14">
              <div className="flex-shrink-0">
                <Link to="/" aria-label="ProCreative Home">
                  <Logo className="h-7 w-auto text-primary dark:text-dark-accent" />
                </Link>
              </div>
              
              <div className="hidden md:flex md:items-center md:space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="text-xs tracking-wider uppercase font-semibold text-foreground/80 dark:text-dark-foreground/80 hover:text-accent dark:hover:text-dark-accent transition-colors"
                  >
                    {item.name}
                  </Link>
                ))}
                <Button 
                  variant="accent" 
                  size="sm"
                  onClick={openGetStartedModal}
                  className="text-xs uppercase tracking-wider font-semibold rounded-full"
                >
                  Get Started
                </Button>
                <ThemeToggle className="ml-2" />
              </div>
              
              <div className="flex items-center md:hidden space-x-2">
                <ThemeToggle />
                <Disclosure.Button 
                  className="inline-flex items-center justify-center p-2 rounded-full text-foreground/80 dark:text-dark-foreground/80 hover:text-accent dark:hover:text-dark-accent hover:bg-secondary/50 dark:hover:bg-dark-secondary/50 focus:outline-none"
                >
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XIcon className="block h-5 w-5" aria-hidden="true" />
                  ) : (
                    <MenuIcon className="block h-5 w-5" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Transition
            enter="transition duration-150 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-100 ease-in"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Disclosure.Panel className="md:hidden mt-2 rounded-2xl bg-white/95 dark:bg-[#060F0C]/95 border border-[#E6E6E3]/60 dark:border-[#111F1B]/60 shadow-xl overflow-hidden backdrop-blur-md">
              <div className="px-4 py-3 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground/80 dark:text-dark-foreground/80 hover:text-accent dark:hover:text-dark-accent hover:bg-secondary/50 dark:hover:bg-dark-secondary/50"
                    onClick={() => close()}
                  >
                    {item.name}
                  </Link>
                ))}
                <Button 
                  variant="accent" 
                  size="md"
                  fullWidth
                  onClick={() => {
                     openGetStartedModal();
                     close();
                  }}
                  className="mt-2 rounded-xl text-xs uppercase tracking-wider font-semibold"
                >
                  Get Started
                </Button>
              </div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
};

export default Header;
